import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import { partnerAccessApi } from '../services/partnerApi';
import { PartnerAccessToken, PartnerClubAssignment } from '../types/partner';

/**
 * Partner dashboard component
 */
const PartnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = usePartnerAuth();
  const [accessTokens, setAccessTokens] = useState<PartnerAccessToken[]>([]);
  const [clubAssignments, setClubAssignments] = useState<PartnerClubAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [tokens, clubs] = await Promise.all([
        partnerAccessApi.getAccessTokens(),
        partnerAccessApi.getClubAssignments(),
      ]);

      setAccessTokens(tokens);
      setClubAssignments(clubs);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/partner/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      <div style={logoContainerStyle}>
        <img
          src="/logo-vp.jpg"
          alt="VoyagePlanner Logo"
          style={logoStyle}
        />
      </div>

      <div style={headerContainerStyle}>
        <div>
          <h1 style={titleStyle}>Partner Dashboard</h1>
          <p style={subtitleStyle}>
            Welcome back, {user?.displayName || user?.email}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => navigate('/partner/matchday-planning')}
            style={upcomingMatchesButtonStyle}
          >
            <Building2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Upcoming Matches
          </button>
          <button onClick={handleLogout} style={logoutButtonStyle}>
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div style={errorBoxStyle}>
          <span style={errorIconStyle}><AlertTriangle size={16} /></span>
          {error}
        </div>
      )}

      {/* User Info Card */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Account Information</h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Email:</span>
            <span style={infoValueStyle}>{user?.email}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Role:</span>
            <span style={{...infoValueStyle, ...roleBadgeStyle}}>
              {user?.role}
            </span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Partner ID:</span>
            <span style={infoValueStyle}>{user?.partnerId}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Email Verified:</span>
            <span style={infoValueStyle}>
              {user?.emailVerified ? <><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Yes</> : <><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> No</>}
            </span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>MFA Enabled:</span>
            <span style={infoValueStyle}>
              {user?.mfaEnabled ? <><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Yes</> : <><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> No</>}
            </span>
          </div>
        </div>
      </div>

      {/* Access Tokens Card */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Access Tokens ({accessTokens.length})</h2>
        {accessTokens.length === 0 ? (
          <p style={emptyStateStyle}>No access tokens assigned yet.</p>
        ) : (
          <div className="table-responsive" style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr style={tableHeaderRowStyle}>
                  <th style={tableHeaderStyle}>Token ID</th>
                  <th style={tableHeaderStyle}>Display Mode</th>
                  <th style={tableHeaderStyle}>Can Update</th>
                  <th style={tableHeaderStyle}>Status</th>
                  <th style={tableHeaderStyle}>Expires At</th>
                </tr>
              </thead>
              <tbody>
                {accessTokens.map((token) => (
                  <tr key={token.id} style={tableRowStyle}>
                    <td style={tableCellStyle}>#{token.id}</td>
                    <td style={tableCellStyle}>{token.displayMode}</td>
                    <td style={tableCellStyle}>
                      {token.canUpdate ? <><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Yes</> : <><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> No</>}
                    </td>
                    <td style={tableCellStyle}>
                      <span style={getStatusBadgeStyle(token.status)}>
                        {token.status}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {new Date(token.expiresAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Club Assignments Card */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Club-Zuordnungen ({clubAssignments.length})</h2>
        {clubAssignments.length === 0 ? (
          <p style={emptyStateStyle}>Noch keine Club-Zuordnungen vorhanden.</p>
        ) : (
          <div className="table-responsive" style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr style={tableHeaderRowStyle}>
                  <th style={tableHeaderStyle}>Club</th>
                  <th style={tableHeaderStyle}>Stadt</th>
                  <th style={tableHeaderStyle}>Stadion</th>
                  <th style={tableHeaderStyle}>Spiele</th>
                </tr>
              </thead>
              <tbody>
                {clubAssignments.map((assignment) => (
                  <tr key={assignment.clubId} style={tableRowStyle}>
                    <td style={tableCellStyle}>
                      <strong>{assignment.clubName}</strong>
                      {assignment.shortName && (
                        <span style={{ color: '#7f8c8d', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                          ({assignment.shortName})
                        </span>
                      )}
                    </td>
                    <td style={tableCellStyle}>{assignment.city || '–'}</td>
                    <td style={tableCellStyle}>{assignment.stadium || '–'}</td>
                    <td style={tableCellStyle}>
                      <span style={matchCountBadgeStyle}>
                        {assignment.matchCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for status badge styling
const getStatusBadgeStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '600',
  };

  switch (status.toLowerCase()) {
    case 'active':
      return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' };
    case 'inactive':
      return { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24' };
    case 'expired':
      return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' };
    default:
      return { ...baseStyle, backgroundColor: '#e2e3e5', color: '#383d41' };
  }
};

// Styles
const containerStyle: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '2rem',
};

const logoContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '2rem',
  paddingTop: '1rem',
};

const logoStyle: React.CSSProperties = {
  maxWidth: '300px',
  height: 'auto',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const headerContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  marginBottom: '0.5rem',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#7f8c8d',
};

const logoutButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#e74c3c',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const upcomingMatchesButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3498db',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  fontSize: '1.2rem',
  color: '#7f8c8d',
};

const errorBoxStyle: React.CSSProperties = {
  backgroundColor: '#fee',
  border: '1px solid #fcc',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1.5rem',
  color: '#c33',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const errorIconStyle: React.CSSProperties = {
  fontSize: '1.2rem',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  padding: '1.5rem',
  marginBottom: '1.5rem',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#2c3e50',
  marginBottom: '1rem',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '1rem',
};

const infoItemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const infoLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: '#7f8c8d',
  fontWeight: '600',
};

const infoValueStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: '#2c3e50',
};

const roleBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.25rem 0.75rem',
  borderRadius: '12px',
  backgroundColor: '#3498db',
  color: 'white',
  fontWeight: '600',
  fontSize: '0.85rem',
};

const emptyStateStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '2rem',
  color: '#7f8c8d',
  fontSize: '1rem',
};

const tableContainerStyle: React.CSSProperties = {
  overflowX: 'auto',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const tableHeaderRowStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
};

const tableHeaderStyle: React.CSSProperties = {
  padding: '0.75rem',
  textAlign: 'left',
  fontWeight: '600',
  color: '#2c3e50',
  borderBottom: '2px solid #dee2e6',
};

const tableRowStyle: React.CSSProperties = {
  borderBottom: '1px solid #dee2e6',
};

const tableCellStyle: React.CSSProperties = {
  padding: '0.75rem',
  color: '#495057',
};

const matchCountBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.25rem 0.75rem',
  borderRadius: '12px',
  backgroundColor: '#3498db',
  color: 'white',
  fontWeight: '600',
  fontSize: '0.85rem',
};

export default PartnerDashboard;

